/**
 * InvalidDataError
 *
 * Custom error class for representing client-side validation/data errors
 * thrown from service layer validation functions before making API calls.
 *
 * This allows callers to reliably differentiate between local validation
 * errors and API/network errors during create/update operations.
 */

import type { ValidationErrorInfo } from '../types/api/errors';
import type { ZodError } from 'zod';

/**
 * Options for constructing an InvalidDataError
 */
export interface InvalidDataErrorOptions {
  /**
   * Structured validation errors for individual fields
   */
  validationErrors?: ValidationErrorInfo[];

  /**
   * Original data object that failed validation (e.g. form or DTO)
   */
  originalData?: unknown;

  /**
   * Additional context for debugging (service name, operation, etc.)
   */
  details?: Record<string, unknown>;
}

/**
 * Error thrown when input data fails client-side validation before an API call.
 */
export class InvalidDataError extends Error {
  public override readonly name = 'InvalidDataError';

  /** Structured validation errors, when available */
  public readonly validationErrors: ValidationErrorInfo[];

  /** The original data that failed validation */
  public readonly originalData?: unknown;

  /** Additional debugging/context metadata */
  public readonly details?: Record<string, unknown>;

  constructor(message: string, options: InvalidDataErrorOptions = {}) {
    super(message);

    this.validationErrors = options.validationErrors ?? [];
    if (options.originalData !== undefined) {
      this.originalData = options.originalData;
    }
    if (options.details !== undefined) {
      this.details = options.details;
    }

    // Restore prototype chain (important when targeting ES5)
    Object.setPrototypeOf(this, InvalidDataError.prototype);
  }

  /**
   * Whether this error contains any field-level validation details.
   */
  public hasValidationErrors(): boolean {
    return this.validationErrors.length > 0;
  }

  /**
   * Get validation errors for a specific field.
   */
  public getFieldErrors(field: string): ValidationErrorInfo[] {
    return this.validationErrors.filter((error) => error.field === field);
  }

  /**
   * Convert the error to a plain object for logging or serialization.
   */
  public toJSON(): {
    name: string;
    message: string;
    validationErrors: ValidationErrorInfo[];
    originalData?: unknown;
    details?: Record<string, unknown>;
  } {
    const result: {
      name: string;
      message: string;
      validationErrors: ValidationErrorInfo[];
      originalData?: unknown;
      details?: Record<string, unknown>;
    } = {
      name: this.name,
      message: this.message,
      validationErrors: this.validationErrors,
    };
    if (this.originalData !== undefined) {
      result.originalData = this.originalData;
    }
    if (this.details !== undefined) {
      result.details = this.details;
    }
    return result;
  }
}

/** How many distinct issue messages to spell out before collapsing to a count. */
const MAX_LISTED_ISSUES = 3;

/**
 * Build an InvalidDataError from a ZodError with a human-readable message.
 *
 * The resulting message is safe to surface directly in the UI: it leads with
 * the given context and lists the first few distinct issue messages instead of
 * Zod's stringified issue array. Field-level details are preserved on
 * `validationErrors` for callers that want to map them back onto a form.
 *
 * @param context - User-facing lead-in, e.g. "Couldn't save the exercise"
 * @param error - The ZodError produced by a failed parse
 * @param options - Extra error options (`originalData`, `details`); the
 *   `validationErrors` are always derived from the ZodError itself
 */
export function invalidDataErrorFromZod(
  context: string,
  error: ZodError,
  options: Omit<InvalidDataErrorOptions, 'validationErrors'> = {}
): InvalidDataError {
  const messages = [...new Set(error.issues.map((issue) => issue.message))];
  const listed = messages.slice(0, MAX_LISTED_ISSUES).join('; ');
  const overflow = messages.length - MAX_LISTED_ISSUES;
  const message = `${context}: ${listed || 'invalid data'}${
    overflow > 0 ? ` (+${overflow} more)` : ''
  }`;

  return new InvalidDataError(message, {
    ...options,
    validationErrors: error.issues.map((issue) => ({
      field: issue.path.join('.'),
      message: issue.message,
      rule: issue.code,
    })),
  });
}

/**
 * Type guard to check whether a value is an InvalidDataError.
 *
 * Supports both instanceof (same runtime) and name-based checks
 * for cases where the error crosses bundle boundaries.
 */
export function isInvalidDataError(error: unknown): error is InvalidDataError {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return (
    error instanceof InvalidDataError ||
    (error as { name?: string }).name === 'InvalidDataError'
  );
}
